import { LOGIN_PRESENTATIONS } from '#pplx/frontend/apps/ask/auth/loginAttemptTypes.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
export const PENDING_LOGIN_ATTEMPT_STORAGE_KEY = 'pplx.pending-login-attempt.v1';
export const LOGIN_ATTEMPT_TTL_MS = 30 * 60 * 1000;
export function persistLoginAttempt(attempt, method, timestamp = Date.now()) {
    const pendingAttempt = {
        attemptId: attempt.attemptId,
        controlPresentation: attempt.controlPresentation,
        resolvedPresentation: attempt.resolvedPresentation,
        experimentEligible: attempt.experimentEligible,
        loginDesignVariation: attempt.loginDesignVariation,
        origin: attempt.origin,
        method,
        timestamp,
    };
    // The attempt starts before an account namespace exists and is consumed after
    // authentication, so it must use one device-global key across that boundary.
    SafeLocalStorage.setItem(PENDING_LOGIN_ATTEMPT_STORAGE_KEY, JSON.stringify(pendingAttempt), {
        global: true,
    });
}
export function clearPendingLoginAttempt(attemptId) {
    if (attemptId) {
        const pendingAttempt = readPendingLoginAttempt();
        if (pendingAttempt?.attemptId !== attemptId) {
            return;
        }
    }
    SafeLocalStorage.removeItem(PENDING_LOGIN_ATTEMPT_STORAGE_KEY, { global: true });
}
export function consumePendingLoginAttempt(now = Date.now()) {
    const pendingAttempt = readPendingLoginAttempt();
    SafeLocalStorage.removeItem(PENDING_LOGIN_ATTEMPT_STORAGE_KEY, { global: true });
    if (!pendingAttempt || now - pendingAttempt.timestamp > LOGIN_ATTEMPT_TTL_MS) {
        return null;
    }
    return {
        attemptId: pendingAttempt.attemptId,
        controlPresentation: pendingAttempt.controlPresentation,
        resolvedPresentation: pendingAttempt.resolvedPresentation,
        experimentEligible: pendingAttempt.experimentEligible,
        loginDesignVariation: pendingAttempt.loginDesignVariation,
        origin: pendingAttempt.origin,
        method: pendingAttempt.method,
        elapsedMs: Math.max(0, now - pendingAttempt.timestamp),
    };
}
export function consumeLoginAttemptForSessionTransition({ previousStatus, status, now = Date.now(), }) {
    if (status === 'unauthenticated' && previousStatus !== 'unauthenticated') {
        clearPendingLoginAttempt();
        return null;
    }
    if (status !== 'authenticated' || previousStatus === 'authenticated') {
        return null;
    }
    return consumePendingLoginAttempt(now);
}
function readPendingLoginAttempt() {
    const value = SafeLocalStorage.getItem(PENDING_LOGIN_ATTEMPT_STORAGE_KEY, { global: true });
    if (!value) {
        return null;
    }
    try {
        const parsed = JSON.parse(value);
        return isPendingLoginAttempt(parsed) ? parsed : null;
    }
    catch {
        return null;
    }
}
function isPendingLoginAttempt(value) {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const pending = value;
    return (typeof pending.attemptId === 'string' &&
        isLoginPresentation(pending.controlPresentation) &&
        isLoginPresentation(pending.resolvedPresentation) &&
        typeof pending.experimentEligible === 'boolean' &&
        (pending.loginDesignVariation === 'control' ||
            pending.loginDesignVariation === 'variant_a' ||
            pending.loginDesignVariation === 'unassigned') &&
        Object.values(ModalOrigin).includes(pending.origin) &&
        (pending.method === 'apple' ||
            pending.method === 'email' ||
            pending.method === 'google' ||
            pending.method === 'workos') &&
        typeof pending.timestamp === 'number' &&
        Number.isFinite(pending.timestamp));
}
function isLoginPresentation(value) {
    return typeof value === 'string' && Object.hasOwn(LOGIN_PRESENTATIONS, value);
}
//# sourceMappingURL=loginAttemptCorrelation.js.map